import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Link2,
  Search,
  Package,
  Store,
  Eye,
  Plus,
  Palette,
  Ruler,
  Sparkles,
  Grid3X3,
  Layers,
  Cookie,
  Hash,
  Boxes,
} from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';
import { DeleteConfirmDialog } from '@/components/molecules/delete-confirm-dialog';
import { useAssociationGroups, useDeleteAssociationGroup } from '@/features/products/hooks/use-associations';
import { getImageUrl } from '@/lib/utils';
import type { VariationType, AssociationGroup } from '@/lib/types';

// Get icon for variation type
function getVariationIcon(variationType: VariationType) {
  switch (variationType) {
    case 'color':
      return Palette;
    case 'size':
      return Ruler;
    case 'size_color':
      return Grid3X3;
    case 'style':
      return Sparkles;
    case 'pattern':
      return Grid3X3;
    case 'material':
      return Layers;
    case 'flavor':
      return Cookie;
    case 'quantity':
      return Hash;
    case 'related':
    default:
      return Boxes;
  }
}

// Get label for variation type
function getVariationLabel(variationType: VariationType, t: (key: string) => string): string {
  switch (variationType) {
    case 'color':
      return t('variations.typeColor');
    case 'size':
      return t('variations.typeSize');
    case 'size_color':
      return t('variations.typeSizeColor');
    case 'style':
      return t('variations.typeStyle');
    case 'pattern':
      return t('variations.typePattern');
    case 'material':
      return t('variations.typeMaterial');
    case 'flavor':
      return t('variations.typeFlavor');
    case 'quantity':
      return t('variations.typeQuantity');
    case 'related':
    default:
      return t('variations.typeRelated');
  }
}

export default function SettingsAssociationsPage() {
  const { t } = useTranslation();
  const { data: groups, isLoading, error } = useAssociationGroups();
  const deleteGroup = useDeleteAssociationGroup();
  const [searchQuery, setSearchQuery] = useState('');

  // Group associations by shop
  const groupedByShop = useMemo(() => {
    if (!groups) return {};

    const filtered = groups.filter((group) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        group.name.toLowerCase().includes(searchLower) ||
        group.description?.toLowerCase().includes(searchLower) ||
        group.shopName?.toLowerCase().includes(searchLower)
      );
    });

    return filtered.reduce((acc, group) => {
      const shopName = group.shopName || t('common.unknownShop');
      if (!acc[shopName]) {
        acc[shopName] = [];
      }
      acc[shopName].push(group);
      return acc;
    }, {} as Record<string, AssociationGroup[]>);
  }, [groups, searchQuery, t]);

  // Statistics
  const stats = useMemo(() => {
    if (!groups) return { totalGroups: 0, totalProducts: 0, byType: {} as Record<string, number> };

    const totalGroups = groups.length;
    const totalProducts = groups.reduce(
      (sum, group) => sum + (group.items?.length || group.products?.length || 0),
      0
    );
    const byType = groups.reduce((acc, group) => {
      const type = group.variationType || 'related';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return { totalGroups, totalProducts, byType };
  }, [groups]);

  const handleDeleteGroup = (groupId: number, groupName: string) => {
    deleteGroup.mutate(groupId, {
      onSuccess: () => {
        toast.success(t('associations.deleteSuccess', { name: groupName }));
      },
      onError: () => {
        toast.error(t('associations.deleteError'));
      },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-9" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/settings">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="size-5" />
            </Button>
          </Link>
          <h1 className="text-2xl font-bold">{t('associations.title')}</h1>
        </div>
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-destructive">{t('common.error')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/settings">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="size-5" />
          </Button>
        </Link>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold truncate">{t('associations.title')}</h1>
          <p className="text-muted-foreground truncate">{t('associations.description')}</p>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Link2 className="size-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalGroups}</p>
                <p className="text-sm text-muted-foreground">{t('associations.totalGroups')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-green-500/10 p-2">
                <Package className="size-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalProducts}</p>
                <p className="text-sm text-muted-foreground">{t('associations.totalProducts')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-500/10 p-2">
                <Store className="size-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{Object.keys(groupedByShop).length}</p>
                <p className="text-sm text-muted-foreground">{t('associations.shopsWithGroups')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder={t('associations.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Groups by Shop */}
      {Object.keys(groupedByShop).length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Link2 className="size-12 mx-auto text-muted-foreground mb-3" />
            <h3 className="font-medium mb-2">{t('associations.noGroups')}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('associations.noGroupsDescription')}
            </p>
            <Link to="/products">
              <Button>
                <Plus className="size-4 mr-2" />
                {t('associations.goToProducts')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedByShop).map(([shopName, shopGroups]) => (
          <Card key={shopName}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Store className="size-4" />
                {shopName}
                <Badge variant="secondary" className="ml-2">
                  {shopGroups.length} {t('associations.groups')}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {shopGroups.map((group) => {
                  const VariationIcon = getVariationIcon(group.variationType);
                  const productCount = group.items?.length || group.products?.length || 0;

                  return (
                    <div
                      key={group.groupId}
                      className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      {/* Variation Type Icon */}
                      <div className="rounded-lg bg-primary/10 p-2 sm:p-2.5 shrink-0">
                        <VariationIcon className="size-4 sm:size-5 text-primary" />
                      </div>

                      {/* Group Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium truncate">{group.name}</h4>
                          <Badge variant="outline" className="text-xs shrink-0">
                            {getVariationLabel(group.variationType, t)}
                          </Badge>
                        </div>
                        {group.description && (
                          <p className="text-sm text-muted-foreground truncate">
                            {group.description}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {productCount} {t('products.productsCount')}
                        </p>
                      </div>

                      {/* Product Previews */}
                      <div className="hidden sm:flex items-center -space-x-2">
                        {(group.items || []).slice(0, 4).map((item, index) => {
                          const imageUrl = item.product.image ? getImageUrl(item.product.image) : null;
                          return (
                            <div
                              key={item.id}
                              className="size-8 rounded-full border-2 border-background bg-muted overflow-hidden"
                              style={{ zIndex: 4 - index }}
                            >
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt=""
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Package className="size-3 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {productCount > 4 && (
                          <div className="size-8 rounded-full border-2 border-background bg-muted flex items-center justify-center text-xs font-medium">
                            +{productCount - 4}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <Link to={`/products?group=${group.groupId}`}>
                          <Button variant="ghost" size="icon">
                            <Eye className="size-4" />
                          </Button>
                        </Link>
                        <DeleteConfirmDialog
                          itemName={group.name}
                          onConfirm={() => handleDeleteGroup(group.groupId, group.name)}
                          isLoading={deleteGroup.isPending}
                          variant="icon"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
